import { FC } from 'react';
import { ReactSVG } from 'react-svg';
import Tooltip from 'react-tooltip-lite';
import type { Discount } from 'types/domain';
import { formatDate } from 'utils/date';
import { useTranslation } from 'utils/i18n';
import {
  DESKTOP_TOOLTIP_DISTANCE,
  MOBILE_TOOLTIP_DISTANCE,
} from '../../constants/breakpoints';
import useIsMobile from '../../hooks/useIsMobile';
import ModalDialog from '../modal';

interface Props {
  isOpen?: boolean;
  onClose?: () => void;
  propertyOrRoomName?: string;
  discounts?: Discount[];
}

const DiscountTermsModal: FC<Props> = ({
  isOpen = false,
  onClose = () => null,
  propertyOrRoomName,
  discounts,
}) => {
  const { t } = useTranslation('common');
  const isMobileView = useIsMobile();

  const discountTooltip = () => {
    return (
      <Tooltip
        content={
          <>
            {t('discount_terms_modal.tooltip_text.following_criteria')}:
            <ul>
              <li>
                {t('discount_terms_modal.tooltip_text.first_criteria')} &quot;
                <span>{t('discount_terms_modal.table.headers.booked_on')}</span>
                &quot; {t('discount_terms_modal.tooltip_text.period')}
              </li>
              <li>
                {t('discount_terms_modal.tooltip_text.second_criteria')} &quot;
                <span>
                  {t('discount_terms_modal.table.headers.occuring_on')}
                </span>
                &quot; {t('discount_terms_modal.tooltip_text.period')}
              </li>
            </ul>
          </>
        }
        className="discount-terms-modal__tooltip-trigger"
        tipContentClassName="discount-terms-modal__tooltip-content"
        distance={
          isMobileView ? MOBILE_TOOLTIP_DISTANCE : DESKTOP_TOOLTIP_DISTANCE
        }
      >
        <ReactSVG
          src="/images/icons/blue-question-mark.svg"
          width={17}
          height={17}
        />
      </Tooltip>
    );
  };

  return (
    <ModalDialog
      isOpen={isOpen}
      headerLabel={t('discount_terms_modal.primary_title')}
      onClose={onClose}
      className="discount-terms-modal"
    >
      <ul>
        <li>{t('discount_terms_modal.list.first')}</li>
        <li>{t('discount_terms_modal.list.second')}</li>
        <li>{t('discount_terms_modal.list.third')}</li>
        <li>{t('discount_terms_modal.list.fourth')}</li>
        <li>{t('discount_terms_modal.list.fifth')}</li>
      </ul>

      <hr />

      <h2 className="discount-terms-modal__table-heading">{`${t(
        'discount_terms_modal.secondary_title'
      )} ${propertyOrRoomName}`}</h2>

      <table>
        <thead>
          <tr>
            <th>{t('discount_terms_modal.table.headers.promotion')}</th>
            <th>{t('discount_terms_modal.table.headers.discount')}</th>
            <th>
              {t('discount_terms_modal.table.headers.booked_on')}
              {discountTooltip()}
            </th>
            <th>
              {t('discount_terms_modal.table.headers.occuring_on')}
              {discountTooltip()}
            </th>
          </tr>
        </thead>
        <tbody>
          {discounts?.map(discount => {
            return (
              <tr key={`discount-terms-modal-row-${discount.id}`}>
                <td>{discount.title}</td>
                <td>{`${discount.amount}%`}</td>
                <td>{`${formatDate(discount.campaignStartDate)} - ${formatDate(
                  discount.campaignEndDate
                )}`}</td>
                <td>{`${formatDate(discount.effectiveStartDate)} - ${formatDate(
                  discount.effectiveEndDate
                )}`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ModalDialog>
  );
};

export default DiscountTermsModal;
